import React, { useCallback, useEffect, useRef, useState } from 'react'
import clsx from 'classnames'
import { uid } from 'utils/uid'
import styles from './ShadowSelect.module.scss'

export type ShadowSelectOption<T extends string | number = string | number> = {
  id: T,
  name: React.ReactNode,
}

type ShadowSelectProps<T extends string | number> = {
  value?: T | '',
  options?: Array<ShadowSelectOption<T>>,
  onChange?: (value: T) => void,
  placeholder?: string,
  shadowColor?: string,
  className?: string,
  tabindex?: number,
  id?: string,
}

const KEY_ENTER = 'Enter'
const ARROW_KEYS = ['ArrowUp', 'ArrowDown']

/** A dropdown styled like ShadowInput; keeps its own value once one is chosen */
export const ShadowSelect = <T extends string | number>(props: ShadowSelectProps<T>) => {
  const { value: initialValue = '', options = [], onChange, placeholder, shadowColor, className, tabindex = 0 } = props
  const [value, setValue] = useState<T | ''>(initialValue)
  const [focused, setFocused] = useState(false)
  const [modalIsOpen, setModalIsOpen] = useState(false)
  const [id] = useState(() => props.id || uid('select'))
  const touchMoveHappened = useRef(false)
  const timeoutId = useRef<ReturnType<typeof setTimeout>>(undefined)

  // Once the parent supplies a value, adopt it (only while nothing is chosen yet)
  useEffect(() => {
    if (!value && initialValue) {
      setValue(initialValue)
    }
  }, [value, initialValue])

  const selectedOption = options.find((el) => el.id === value)
  const displayValue = selectedOption ? selectedOption.name : placeholder

  const handleDocumentTouchMove = useCallback(() => {
    touchMoveHappened.current = true
  }, [])

  const closeModalRef = useRef<() => void>(() => undefined)

  const handleDocumentTouchEnd = useCallback((e: TouchEvent) => {
    const target = e.target as HTMLElement
    const clickedAway = !target.closest('#' + id)
    if (clickedAway && !touchMoveHappened.current) {
      timeoutId.current = setTimeout(() => closeModalRef.current(), 0)
      const tag = target.tagName.toLowerCase()
      if (tag !== 'input' && tag !== 'button' && !target.closest('[data-active-element]')) {
        e.stopPropagation()
        e.preventDefault()
      }
      return
    }
    touchMoveHappened.current = false
  }, [id])

  const openModal = useCallback(() => {
    setModalIsOpen(true)
    document.addEventListener('touchend', handleDocumentTouchEnd, true)
    document.addEventListener('touchmove', handleDocumentTouchMove, true)
  }, [handleDocumentTouchEnd, handleDocumentTouchMove])

  const closeModal = useCallback(() => {
    setModalIsOpen(false)
    document.removeEventListener('touchend', handleDocumentTouchEnd, true)
    document.removeEventListener('touchmove', handleDocumentTouchMove, true)
  }, [handleDocumentTouchEnd, handleDocumentTouchMove])
  closeModalRef.current = closeModal

  useEffect(() => () => {
    clearTimeout(timeoutId.current)
    closeModalRef.current()
  }, [])

  const handleChange = (newValue: T) => {
    setValue(newValue)
    closeModal()
    onChange?.(newValue)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === KEY_ENTER) {
      openModal()
    }
    if (ARROW_KEYS.includes(e.key)) {
      e.preventDefault()
    }
  }

  const handleBlur = () => {
    setFocused(false)
    closeModal()
  }

  return (
    <div
      id={id}
      className={clsx([styles.inputContainer, className, { [styles.focused]: focused }])}
      data-active-element
      tabIndex={tabindex}
      onFocus={() => setFocused(true)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    >
      <div className={clsx([styles.input, { [styles.isOpen]: modalIsOpen }])} onClick={openModal}>
        <div className={styles.inputPlaceholder}>
          {modalIsOpen ? '' : value ? displayValue : placeholder}
        </div>
        <div className={styles.optionsContainer}>
          {options.map((option) => (
            <div
              key={option.id}
              data-value={option.id}
              className={styles.option}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handleChange(option.id)
              }}
            >
              {option.name}
            </div>
          ))}
        </div>
      </div>
      <div data-color={shadowColor} className={styles.shadow} />
    </div>
  )
}
